import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Button, Alert,
  CircularProgress,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Chip
} from '@mui/material';
import {
  Description as DescriptionIcon,
  Download as DownloadIcon,
  Print as PrintIcon,
  School as SchoolIcon,
  CheckCircle as PassIcon,
  Cancel as FailIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import Loading from '../common/Loading';
import { studentAPI } from '../../services/api';
import { DocumentSecurity } from '../../services/documentSecurity';

const SEM_NAMES_AR = {
  S1: 'السداسي الأول',   S2: 'السداسي الثاني',
  S3: 'السداسي الثالث',  S4: 'السداسي الرابع',
  S5: 'السداسي الخامس',  S6: 'السداسي السادس',
  S7: 'السداسي السابع',  S8: 'السداسي الثامن',
  S9: 'السداسي التاسع',  S10: 'السداسي العاشر',
};
const SEM_NAMES_FR = {
  S1: 'Semestre 1', S2: 'Semestre 2',
  S3: 'Semestre 3', S4: 'Semestre 4',
  S5: 'Semestre 5', S6: 'Semestre 6',
  S7: 'Semestre 7', S8: 'Semestre 8',
  S9: 'Semestre 9', S10: 'Semestre 10',
};
const SEM_COLORS = {
  S1: '#2563eb', S2: '#d97706', S3: '#16a34a',
  S4: '#7c3aed', S5: '#dc2626', S6: '#0891b2',
  S7: '#2563eb', S8: '#d97706', S9: '#16a34a', S10: '#7c3aed',
};

const PASS_CODES = new Set(['V', 'VAR', 'ACR', 'AC', 'VE', 'VAD']);
const FAIL_CODES = new Set(['AJ', 'NV', 'EL', 'RE', 'AB', 'NA']);

// Returns { bg, color, isPass } for a cod_tre / grade combination
const resultStyle = (cod_tre, not_elp) => {
  if (cod_tre && PASS_CODES.has(cod_tre)) return { bg: '#dcfce7', color: '#15803d', isPass: true };
  if (cod_tre && FAIL_CODES.has(cod_tre)) return { bg: '#fee2e2', color: '#dc2626', isPass: false };
  // no cod_tre — derive from grade
  const pass = not_elp !== null && parseFloat(not_elp) >= 10;
  return pass
    ? { bg: '#dcfce7', color: '#15803d', isPass: true }
    : { bg: '#fee2e2', color: '#dc2626', isPass: false };
};

/* ─────────────────────────────────────────── */

const OfficialDocuments = () => {
  const [documentsData, setDocumentsData] = useState(null);
  const [isLoading, setIsLoading]         = useState(true);
  const [error, setError]                 = useState(null);
  const [generating, setGenerating]       = useState(null); // semesterCode being generated
  const { user } = useAuth();

  /* ── helpers ── */
  const getAcademicYear = (subjects) => {
    const counts = {};
    subjects.forEach(s => { if (s.cod_anu) counts[s.cod_anu] = (counts[s.cod_anu] || 0) + 1; });
    const years = Object.keys(counts);
    if (!years.length) return null;
    return years.sort((a, b) => counts[b] - counts[a])[0];
  };

  const processData = (raw) => {
    // Backend already excludes CF/CC and yearly-module codes, and filters not_elp IS NOT NULL.
    // Here we only deduplicate per module code: if the same cod_elp appears in both session 1
    // and session 2 (rattrapage), keep the session 2 entry (final deliberation result).
    const out = {};
    for (const sem in raw) {
      const subjects = raw[sem].subjects || [];

      const map = new Map();
      subjects.forEach(s => {
        const prev = map.get(s.cod_elp);
        if (!prev) {
          map.set(s.cod_elp, s);
        } else {
          // Prefer session 2 (rattrapage deliberation) over session 1
          const curSes  = parseInt(s.cod_ses)    || 1;
          const prevSes = parseInt(prev.cod_ses) || 1;
          const cur  = s.not_elp   !== null ? parseFloat(s.not_elp)    : -1;
          const prevG = prev.not_elp !== null ? parseFloat(prev.not_elp) : -1;
          if (curSes > prevSes || (curSes === prevSes && cur > prevG)) map.set(s.cod_elp, s);
        }
      });

      const list   = Array.from(map.values()).sort((a, b) => (a.lib_elp || '').localeCompare(b.lib_elp || ''));
      const graded = list.filter(s => s.not_elp !== null);
      const passed = graded.filter(s => parseFloat(s.not_elp) >= 10);
      const yr     = raw[sem].academicYear || getAcademicYear(list);

      // Use the semester element's own grade from RESULTAT_ELP (official jury average).
      // Fall back to a calculated average only when no semester-level grade exists.
      const semGrade = raw[sem].semester_grade || null;
      // Fetched semester grade: keep exact value from Oracle (no rounding).
      // Calculated fallback: round to 2 dp since it's derived, not official.
      // Fetched semester grade: raw string from DB — never parse/reformat so Oracle's
      // exact decimal places (e.g. 10.285) are preserved unchanged.
      // Calculated fallback only used when Oracle has no semester-level grade.
      const avg = semGrade?.not_elp != null
        ? semGrade.not_elp          // already a string from backend
        : graded.length
          ? (graded.reduce((a, s) => a + parseFloat(s.not_elp), 0) / graded.length).toFixed(2)
          : null;

      out[sem] = {
        subjects:        list,
        academicYear:    yr,
        total:           list.length,
        graded:          graded.length,
        passed:          passed.length,
        failed:          graded.length - passed.length,
        avg,
        avg_cod_tre:     semGrade?.cod_tre || null,
        passRate:        graded.length ? Math.round((passed.length / graded.length) * 100) : 0,
        specialization:  raw[sem].specialization || null,
      };
    }
    return out;
  };

  const fetchDocuments = async () => {
    try {
      setIsLoading(true); setError(null);
      const res = await studentAPI.getOfficialDocuments({});
      setDocumentsData(processData(res.documents || {}));
    } catch (e) {
      setError(e.response?.data?.error || 'Échec du chargement');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchDocuments(); }, []);

  /* ── PDF generation (unchanged logic, same output) ── */
  const generatePDF = async (semesterCode, semesterData) => {
    const jsPDF  = (await import('jspdf')).default;
    await import('jspdf-autotable');
    const doc = new jsPDF('p', 'mm', 'a4');
    const W   = doc.internal.pageSize.getWidth();
    const H   = doc.internal.pageSize.getHeight();

    try {
      const fontRes = await fetch('/fonts/Amiri-Regular.ttf');
      if (fontRes.ok) {
        const blob   = await fontRes.blob();
        const b64    = await new Promise((res, rej) => {
          const r = new FileReader();
          r.onloadend = () => res(r.result.split(',')[1]);
          r.onerror   = rej;
          r.readAsDataURL(blob);
        });
        doc.addFileToVFS('Amiri-Regular.ttf', b64);
        doc.addFont('Amiri-Regular.ttf', 'Amiri', 'normal');
        doc.addFont('Amiri-Regular.ttf', 'Amiri', 'bold');
      }
    } catch { /* fallback to default */ }

    doc.setFont('Amiri', 'normal');
    const primary = [44, 62, 80];
    const text    = [33, 37, 41];

    // Header band
    doc.setFillColor(...primary);
    doc.rect(0, 0, W, 35, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(14); doc.setFont('Amiri', 'bold');
    doc.text('FACULTÉ DES SCIENCES JURIDIQUES ET POLITIQUES', W / 2, 13, { align: 'center' });
    doc.setFont('Amiri', 'normal'); doc.setFontSize(10);
    doc.text('UNIVERSITÉ HASSAN 1ER - SETTAT', W / 2, 21, { align: 'center' });
    doc.text('كلية العلوم القانونية والسياسية - سطات', W / 2, 29, { align: 'center' });

    // Title
    doc.setTextColor(...text);
    doc.setFontSize(20); doc.setFont('Amiri', 'bold');
    doc.text('RELEVÉ DE NOTES', W / 2, 48, { align: 'center' });
    doc.setFontSize(14); doc.setFont('Amiri', 'normal');
    doc.text('كشف النقط', W / 2, 57, { align: 'center' });

    const semLabel = `${SEM_NAMES_FR[semesterCode] || semesterCode} — ${SEM_NAMES_AR[semesterCode] || ''}`;
    doc.setFontSize(12);
    doc.text(semLabel, W / 2, 66, { align: 'center' });

    const yr = semesterData.academicYear;
    if (yr) {
      doc.setFontSize(10);
      doc.text(`Année Universitaire ${yr}/${parseInt(yr) + 1}`, W / 2, 74, { align: 'center' });
    }

    // Student info
    const dob = user?.date_naissance ? new Date(user.date_naissance).toLocaleDateString('fr-FR') : '-';
    const birthPlace = user?.lieu_naissance || '-';
    const program = semesterData.specialization || user?.etape || '-';
    const infoBody = [
      ['Nom & Prénom / الاسم:', user?.nom_complet || '-'],
      ['Date de naissance / تاريخ الازدياد:', `${dob}  —  ${birthPlace}`],
      ['CNE / رقم التعريف الوطني للطالب:', user?.cod_nne_ind || '-'],
      ['N° Apogée / رقم الطالب:', user?.cod_etu || '-'],
      ['CIN:', user?.cin || '-'],
      ['Inscrit en / مسجل في:', program],
    ];
    doc.autoTable({
      body: infoBody,
      startY: 82, theme: 'plain',
      styles: { fontSize: 9, cellPadding: 1.5, font: 'Amiri' },
      columnStyles: { 0: { fontStyle: 'bold', cellWidth: 75 } },
    });

    // Grades table
    const rows = semesterData.subjects.map(s => {
      const name = (s.lib_elp_arb && s.lib_elp_arb.trim()) ? s.lib_elp_arb : (s.lib_elp || s.cod_elp);
      const ses = s.cod_ses ? String(s.cod_ses) : '1';
      const yr = s.cod_anu ? `${s.cod_anu}/${String(parseInt(s.cod_anu) + 1).slice(-2)}` : '';
      const sesLabel = `S${ses}${yr ? ' ' + yr : ''}`;
      return [
        name,
        s.not_elp !== null ? `${parseFloat(s.not_elp).toFixed(2)}/20` : 'ABS',
        sesLabel,
        s.cod_tre || '-',
      ];
    });

    const avgResultLabel = semesterData.avg_cod_tre || (parseFloat(semesterData.avg) >= 10 ? 'Validé' : 'Non Validé');
    const avgRow = semesterData.avg !== null
      ? [{ content: 'Moyenne du semestre — معدل السداسي', colSpan: 1, styles: { fontStyle: 'bold', fillColor: [240, 245, 255] } },
         { content: '', colSpan: 1, styles: { fillColor: [240, 245, 255] } },
         { content: `${semesterData.avg}/20`, colSpan: 1, styles: { fontStyle: 'bold', halign: 'center', fillColor: primary, textColor: 255, fontSize: 10 } },
         { content: avgResultLabel, colSpan: 1, styles: { fontStyle: 'bold', halign: 'center', fillColor: [240, 245, 255] } }]
      : null;

    doc.autoTable({
      startY: doc.lastAutoTable.finalY + 8,
      head: [['Module / المادة', 'Note/Barème', 'Session', 'Résultat']],
      body: rows,
      foot: avgRow ? [avgRow] : [],
      theme: 'grid',
      styles: { font: 'Amiri', fontSize: 9, cellPadding: 2.5, textColor: text, lineColor: [220, 220, 220], lineWidth: 0.2 },
      headStyles: { fillColor: primary, textColor: 255, fontStyle: 'bold', halign: 'center' },
      footStyles: { fillColor: [240, 245, 255], textColor: text, lineColor: primary, lineWidth: 0.5 },
      columnStyles: { 0: { cellWidth: 85 }, 1: { halign: 'center', cellWidth: 28 }, 2: { halign: 'center', cellWidth: 40 }, 3: { halign: 'center', cellWidth: 22 } },
    });

    // QR / signature
    const docData = { studentId: user?.cod_etu, semester: semesterCode, timestamp: new Date().toISOString(), modules: semesterData.subjects };
    const { signature: sig, qrCodeDataUrl: qr } = await DocumentSecurity.signDocument(docData);

    if (qr) {
      try { doc.addImage(qr.split(',')[1], 'PNG', W - 40, H - 50, 30, 30); } catch { /* skip */ }
    }

    doc.setFont('Amiri', 'normal'); doc.setFontSize(7.5); doc.setTextColor(110);
    doc.text(`ID: DOC-${user?.cod_etu}-${semesterCode}-${Date.now()} | Sig: ${sig}`, 15, H - 32);
    doc.text('Vérifiez l\'authenticité de ce document via le QR code.', 15, H - 27);
    doc.setTextColor(150);
    doc.text('هذا الكشف للاطلاع فقط ولا يُعتبر وثيقة رسمية إلا بعد المصادقة عليه من الجهة المختصة.', W / 2, H - 18, { align: 'center' });
    doc.text(`Généré le ${new Date().toLocaleString('fr-FR')}`, W / 2, H - 12, { align: 'center' });

    return doc;
  };

  const handleDownload = async (semCode, semData) => {
    setGenerating(semCode); setError(null);
    try {
      const doc = await generatePDF(semCode, semData);
      doc.save(`Releve_${semCode}_${user.cod_etu}.pdf`);
    } catch { setError('Erreur lors de la génération du PDF.'); }
    finally { setGenerating(null); }
  };

  const handlePrint = async (semCode, semData) => {
    setGenerating(semCode); setError(null);
    try {
      const doc = await generatePDF(semCode, semData);
      doc.output('dataurlnewwindow');
    } catch { setError('Erreur lors de la préparation du document.'); }
    finally { setGenerating(null); }
  };

  /* ── render ── */
  if (isLoading) return <Loading message="Chargement des relevés de notes..." />;

  const semesters = documentsData
    ? Object.entries(documentsData).sort(([a], [b]) => a.localeCompare(b))
    : [];

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>

      {/* Page header */}
      <Box sx={{
        background: 'linear-gradient(135deg, #1a2a3a 0%, #2c3e50 60%, #3d566e 100%)',
        borderRadius: 4, p: { xs: 2.5, md: 3.5 }, mb: 3, color: 'white',
        display: 'flex', alignItems: 'center', gap: 2,
      }}>
        <Box sx={{ width: 52, height: 52, borderRadius: 3, bgcolor: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <DescriptionIcon sx={{ fontSize: 28 }} />
        </Box>
        <Box>
          <Typography variant="h5" fontWeight={700} lineHeight={1.2}>كشوفات النقط</Typography>
          <Typography variant="body2" sx={{ opacity: 0.75, mt: 0.3 }}>Relevés de notes — téléchargez ou imprimez votre relevé par semestre</Typography>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Empty state */}
      {semesters.length === 0 && (
        <Paper elevation={0} sx={{ p: 6, textAlign: 'center', borderRadius: 4, border: '1.5px dashed #e0e0e0' }}>
          <SchoolIcon sx={{ fontSize: 48, color: '#cbd5e1', mb: 1.5 }} />
          <Typography variant="h6" color="text.secondary" fontWeight={600} gutterBottom>
            Aucun relevé disponible
          </Typography>
          <Typography variant="body2" color="text.disabled">
            Les relevés apparaîtront ici dès que vos notes seront disponibles.
          </Typography>
        </Paper>
      )}

      {/* Semester blocks */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {semesters.map(([semCode, semData]) => {
          const color     = SEM_COLORS[semCode] || '#64748b';
          const isWorking = generating === semCode;
          const yr        = semData.academicYear;

          return (
            <Paper key={semCode} elevation={0} sx={{ borderRadius: 3, border: `1.5px solid ${color}33`, overflow: 'hidden' }}>

              {/* Header */}
              <Box sx={{
                px: 2.5, py: 2, bgcolor: `${color}10`, borderBottom: `1.5px solid ${color}22`,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5,
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{
                    width: 44, height: 44, borderRadius: 2, bgcolor: color, flexShrink: 0,
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <Typography sx={{ fontSize: '0.5rem', fontWeight: 700, color: 'white', opacity: 0.85, lineHeight: 1 }}>S</Typography>
                    <Typography sx={{ fontSize: '1.1rem', fontWeight: 900, color: 'white', lineHeight: 1 }}>
                      {semCode.replace('S', '')}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" fontWeight={800} sx={{ color, lineHeight: 1.2 }}>
                      {SEM_NAMES_AR[semCode] || semCode}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {SEM_NAMES_FR[semCode]}{yr ? ` · ${yr} / ${parseInt(yr) + 1}` : ''}{semData.specialization ? ` · ${semData.specialization}` : ''}
                    </Typography>
                  </Box>
                </Box>

                {/* Action buttons — print temporarily disabled */}
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button variant="outlined" size="small" startIcon={<PrintIcon />} disabled
                    sx={{ borderRadius: 2 }}>
                    Imprimer
                  </Button>
                  <Button variant="contained" size="small"
                    startIcon={isWorking ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
                    disabled={generating !== null}
                    onClick={() => handleDownload(semCode, semData)}
                    sx={{ borderRadius: 2, boxShadow: 'none' }}>
                    PDF
                  </Button>
                </Box>
              </Box>

              {/* Grades table */}
              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table size="small" sx={{ minWidth: 400 }}>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                      <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '0.78rem', py: 1.2 }}>المادة / Module</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '0.78rem', py: 1.2, fontFamily: 'monospace' }}>Code</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, color: '#475569', fontSize: '0.78rem', py: 1.2 }}>Note /20</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, color: '#475569', fontSize: '0.78rem', py: 1.2 }}>Résultat</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {semData.subjects.map((s, i) => {
                      const name  = (s.lib_elp_arb && s.lib_elp_arb.trim()) ? s.lib_elp_arb : (s.lib_elp || s.cod_elp);
                      const grade = s.not_elp !== null ? parseFloat(s.not_elp) : null;
                      const rs    = resultStyle(s.cod_tre, s.not_elp);
                      return (
                        <TableRow key={i} sx={{ '&:nth-of-type(even)': { bgcolor: '#fafafa' }, '&:hover': { bgcolor: `${color}08` } }}>
                          <TableCell sx={{ py: 1.2 }}>
                            <Typography variant="body2" fontWeight={600} sx={{ color: '#1e293b' }}>{name}</Typography>
                          </TableCell>
                          <TableCell sx={{ py: 1.2, fontFamily: 'monospace', color: '#64748b', fontSize: '0.78rem' }}>
                            {s.cod_elp}
                          </TableCell>
                          <TableCell align="center" sx={{ py: 1.2 }}>
                            {grade !== null ? (
                              <Box sx={{
                                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                minWidth: 52, height: 28, borderRadius: 1.5, fontWeight: 800, fontSize: '0.85rem',
                                bgcolor: rs.bg,
                                color: rs.color,
                                border: `1px solid ${rs.isPass ? '#86efac' : '#fca5a5'}`,
                              }}>
                                {grade.toFixed(2)}
                              </Box>
                            ) : (
                              <Typography variant="caption" color="text.disabled" fontWeight={600}>ABS</Typography>
                            )}
                          </TableCell>
                          <TableCell align="center" sx={{ py: 1.2 }}>
                            {s.cod_tre ? (
                              <Chip
                                label={s.cod_tre}
                                size="small"
                                icon={rs.isPass ? <PassIcon sx={{ fontSize: '13px !important' }} /> : <FailIcon sx={{ fontSize: '13px !important' }} />}
                                sx={{
                                  height: 22, borderRadius: 1.5, fontWeight: 700, fontSize: '0.7rem',
                                  bgcolor: rs.bg, color: rs.color,
                                }}
                              />
                            ) : <Typography variant="caption" color="text.disabled">—</Typography>}
                          </TableCell>
                        </TableRow>
                      );
                    })}

                    {/* Semester average row */}
                    {semData.avg !== null && (
                      <TableRow sx={{ bgcolor: `${color}14`, borderTop: `2px solid ${color}55` }}>
                        <TableCell colSpan={2} sx={{ py: 1.5, pl: 2 }}>
                          <Typography variant="body2" fontWeight={800} sx={{ color, letterSpacing: 0.3 }}>
                            Moyenne du semestre — معدل السداسي
                          </Typography>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 1.5 }}>
                          <Box sx={{
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            minWidth: 60, height: 32, borderRadius: 2, fontWeight: 900, fontSize: '1rem',
                            bgcolor: color, color: 'white',
                            boxShadow: `0 2px 8px ${color}66`,
                          }}>
                            {semData.avg}/20
                          </Box>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 1.5 }}>
                          {(() => {
                            const rs = resultStyle(semData.avg_cod_tre, semData.avg);
                            const label = semData.avg_cod_tre || (rs.isPass ? 'Validé' : 'Non Validé');
                            return (
                              <Chip
                                label={label}
                                size="small"
                                icon={rs.isPass
                                  ? <PassIcon sx={{ fontSize: '13px !important' }} />
                                  : <FailIcon sx={{ fontSize: '13px !important' }} />}
                                sx={{
                                  height: 24, borderRadius: 1.5, fontWeight: 800, fontSize: '0.72rem',
                                  bgcolor: rs.bg, color: rs.color,
                                }}
                              />
                            );
                          })()}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

            </Paper>
          );
        })}
      </Box>
    </Box>
  );
};

export default OfficialDocuments;
